"use client"

import { useRef, useState } from "react"
import type { GachaMachineView } from "./gacha-data"

type GachaMachineMediaProps = {
  machine: GachaMachineView
  isPending?: boolean
  fit?: "contain" | "cover"
  onAspectRatioChange?: (aspectRatio: string) => void
  preloadMachines?: readonly GachaMachineView[]
}

type VideoState = {
  readyCodes: ReadonlySet<string>
  visibleCode: string | null
}

function MachineMediaSkeleton() {
  return (
    <div
      className="absolute inset-0 animate-pulse bg-foreground/10"
      aria-hidden="true"
    />
  )
}

export function GachaMachineMedia({
  machine,
  isPending: _isPending = false,
  fit = "cover",
  onAspectRatioChange,
  preloadMachines = [],
}: GachaMachineMediaProps) {
  const requestedVideoCodeRef = useRef(machine.code)
  requestedVideoCodeRef.current = machine.code
  const [videoState, setVideoState] = useState<VideoState>(() => ({
    readyCodes: new Set<string>(),
    visibleCode: null,
  }))
  const objectFitClassName =
    fit === "contain" ? "object-contain" : "object-cover"

  if (machine.videoSrc) {
    const videoMachines = [machine, ...preloadMachines].filter(
      (candidate, index, candidates) =>
        candidate.videoSrc &&
        candidates.findIndex(
          (otherCandidate) => otherCandidate.code === candidate.code,
        ) === index,
    )
    const visibleVideoCode = videoState.readyCodes.has(machine.code)
      ? machine.code
      : videoState.visibleCode
    const visibleMachine =
      videoMachines.find((candidate) => candidate.code === visibleVideoCode) ??
      machine
    const visibleVideoReady = videoState.readyCodes.has(visibleMachine.code)

    const markVideoReady = (machineCode: string) => {
      setVideoState((current) => {
        if (current.readyCodes.has(machineCode)) return current

        return {
          readyCodes: new Set([...current.readyCodes, machineCode]),
          visibleCode:
            requestedVideoCodeRef.current === machineCode
              ? machineCode
              : current.visibleCode,
        }
      })
    }

    return (
      <>
        <div className="absolute inset-0 bg-foreground/5" />
        {!visibleVideoReady ? <MachineMediaSkeleton /> : null}
        {videoMachines.map((videoMachine) => {
          const isVisible = videoMachine.code === visibleMachine.code
          const isReady = videoState.readyCodes.has(videoMachine.code)

          return (
            <video
              key={videoMachine.code}
              aria-hidden={isVisible ? undefined : true}
              aria-label={isVisible ? videoMachine.name : undefined}
              autoPlay
              className={[
                "absolute inset-0 h-full w-full object-center transition-opacity duration-200",
                objectFitClassName,
                isVisible && isReady ? "opacity-100" : "opacity-0",
              ].join(" ")}
              loop
              muted
              playsInline
              poster={videoMachine.thumbnailUrl}
              preload="auto"
              tabIndex={-1}
              onLoadedMetadata={(event) => {
                if (requestedVideoCodeRef.current !== videoMachine.code) return

                const { videoWidth, videoHeight } = event.currentTarget
                if (videoWidth > 0 && videoHeight > 0) {
                  onAspectRatioChange?.(`${videoWidth} / ${videoHeight}`)
                }
              }}
              onLoadedData={() => markVideoReady(videoMachine.code)}
              onCanPlay={() => markVideoReady(videoMachine.code)}
              onError={() => markVideoReady(videoMachine.code)}
            >
              {videoMachine.videoSrc.endsWith(".webm") ? (
                <>
                  <source src={videoMachine.videoSrc} type="video/webm" />
                  <source src={videoMachine.videoHevc} type="video/mp4" />
                </>
              ) : (
                <source src={videoMachine.videoSrc} type="video/mp4" />
              )}
            </video>
          )
        })}
      </>
    )
  }

  return (
    <>
      {machine.thumbnailUrl ? (
        <img
          src={machine.thumbnailUrl}
          alt={machine.name}
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget
            if (naturalWidth > 0 && naturalHeight > 0) {
              onAspectRatioChange?.(`${naturalWidth} / ${naturalHeight}`)
            }
          }}
          className={[
            "absolute inset-0 h-full w-full object-center",
            objectFitClassName,
          ].join(" ")}
        />
      ) : (
        <div className="absolute inset-0 bg-foreground/5" />
      )}
    </>
  )
}
